<i18n
  src="@/locales/views/agro_exporter/certifications/providers/show_producer.yml"
  lang="yaml"
></i18n>
<i18n src="@/locales/models/crop_field_certification.yml" lang="yaml"></i18n>

<template>
  <go-back-link
    :text="$t('labels.go_back_link')"
    :name="routeName('list_providers')"
    :params="{
      agro_exporter_id: agro_exporter_id
    }"
  ></go-back-link>
  <bread-crumbs :labels="[module_name, producer?.name]"></bread-crumbs>
  <template v-if="load_data_general">
    <div class="flex flex-row justify-end">
      <div class="panel-plain-label">
        <badge-default
          :label="
            crop_field_certifications.length +
            ' ' +
            $t('labels.total_number_crop_field_certifications')
          "
        />
      </div>
      <div class="grow"></div>
      <new-button
        v-if="producer.manage_certifications"
        @click.prevent="goToNewCropFieldCertifications()"
        :label="$t('labels.new_crop_field_certifications_button')"
      />
    </div>
    <div class="mt-8 flex flex-col">
      <alert-danger :errors_data="std_err" />
      <div class="table-first-container">
        <div class="table-second-container">
          <div class="table-third-container">
            <table class="table">
              <thead class="table-header">
                <tr>
                  <th scope="col" class="table-header-cell">
                    {{ $t("model.crop_field_certification.crop_field_id") }}
                  </th>
                  <th scope="col" class="table-header-cell">
                    {{
                      $t("model.crop_field_certification.certification_id")
                    }}
                  </th>
                  <th scope="col" class="table-header-cell">
                    {{ $t("model.crop_field_certification.renewal_date") }}
                  </th>
                  <th scope="col" class="table-header-cell">
                    {{ $t("model.crop_field_certification.expiration_date") }}
                  </th>
                  <th scope="col" class="table-header-cell">
                    {{ $t("model.crop_field_certification.active") }}
                  </th>
                  <th scope="col" class="table-header-cell">
                    {{ $t("model.crop_field_certification.expired_at") }}
                  </th>
                  <th scope="col" class="table-header-cell">
                    <i class="fa-solid fa-paperclip"></i>
                  </th>
                  <th
                    scope="col"
                    class="table-header-cell"
                    v-if="producer.manage_certifications"
                  ></th>
                </tr>
              </thead>
              <tbody class="table-body">
                <tr
                  class="table-row"
                  v-for="crop_field_certification in crop_field_certifications"
                  :key="crop_field_certification.id"
                >
                  <td class="table-cell">
                    {{ crop_field_certification.crop_field.name }}
                  </td>
                  <td class="table-cell">
                    {{ crop_field_certification.certification.name }}
                  </td>
                  <td class="table-cell">
                    {{ dateFormatter(crop_field_certification.renewal_date) }}
                  </td>
                  <td class="table-cell">
                    {{
                      dateFormatter(crop_field_certification.expiration_date)
                    }}
                  </td>
                  <td class="table-cell">
                    <yes-no-label :value="crop_field_certification.active" />
                  </td>
                  <td class="table-cell">
                    <div
                      v-if="crop_field_certification.active"
                      v-html="expiredAt(crop_field_certification)"
                    ></div>
                  </td>
                  <td class="table-cell">
                    <i
                      v-if="crop_field_certification.certificate_filename"
                      :class="[
                        'fa-solid',
                        crop_field_certification.downloading_certificate
                          ? 'fa-sync fa-spin'
                          : 'fa-download'
                      ]"
                      @click.prevent="
                        downloadCertificateFile(crop_field_certification)
                      "
                    ></i>
                  </td>
                  <td
                    v-if="producer.manage_certifications"
                    class="table-cell-action"
                  >
                    <destroy-button
                      v-if="!crop_field_certification.proc_button_destroy"
                      :disabled_button="false"
                      @destroyEvent="
                        destroyCropFieldCertification(
                          crop_field_certification
                        )
                      "
                      :label="$t('labels.destroy_button_label')"
                      :title="
                        $t('labels.destroy_button_title', {
                          certification_name:
                            crop_field_certification.certification.name
                        })
                      "
                      :message="$t('labels.destroy_button_message')"
                      :label_confirm="$t('labels.destroy_button_label_confirm')"
                    />
                    <destroy-button-processing
                      v-else
                      :label="$t('labels.destroy_button_label')"
                    />
                    <span class="space-actions"></span>
                    <edit-button
                      @click.prevent="
                        goToEditCropFieldCertification(
                          crop_field_certification.id
                        )
                      "
                      :label="$t('labels.edit_button')"
                    />
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  </template>
</template>

<script>
import Requester from "@/views/agro_exporter/requesters/certifications/providers/manage_crop_field_certification.js";

export default {
  name: "ShowOrganization",
  data() {
    return {
      load_data_general: false,
      proc_button_destroy: false,
      producer: {},
      crop_field_certifications: []
    };
  },
  props: {
    agro_exporter_id: { required: true },
    producer_id: { required: true }
  },
  created() {
    this.checkIfAuthenticated().then(() =>
      this.checkIfAuthorized({
        role: this.$route.meta.role,
        permission: this.$route.meta.permission,
        entity: {
          agro_exporter_id: this.agro_exporter_id
        }
      }).then(() => this.loadData())
    );
  },
  methods: {
    async loadData() {
      await this.fetchProducer();
      await this.fetchOrganizationCertifications();
      this.load_data_general = true;
    },
    async fetchProducer() {
      let response = await Requester.fetchProducer(
        this.agro_exporter_id,
        this.producer_id
      );

      this.producer = response.data;
    },
    async fetchOrganizationCertifications() {
      let response = await Requester.fetchCropFieldCertifications(
        this.agro_exporter_id,
        this.producer_id
      );

      this.crop_field_certifications = response.data;
      this.crop_field_certifications.forEach(crop_field_certif => {
        crop_field_certif["proc_button_destroy"] = false;
        crop_field_certif["downloading_certificate"] = false;
      });
    },
    async downloadCertificateFile(crop_field_certification) {
      crop_field_certification.downloading_certificate = true;
      try {
        var response = await Requester.downloadCertificateFile(
          this.agro_exporter_id,
          this.producer_id,
          crop_field_certification.id
        );
        const file = response.data;
        var fileURL = window.URL.createObjectURL(new Blob([file]));
        var fileLink = document.createElement("a");

        fileLink.href = fileURL;
        fileLink.setAttribute(
          "download",
          crop_field_certification.certificate_filename
        );
        document.body.appendChild(fileLink);

        fileLink.click();
      } catch (error) {
        error;
      }
      crop_field_certification.downloading_certificate = false;
    },
    async destroyCropFieldCertification(crop_field_certification) {
      crop_field_certification.proc_button_destroy = true;
      try {
        let response = await Requester.destroy(
          this.agro_exporter_id,
          this.producer_id,
          crop_field_certification.id
        );
        this.registerSuccessNotification({
          title: response.data.success_message
        });
        this.crop_field_certifications.splice(
          this.crop_field_certifications.indexOf(crop_field_certification),
          1
        );
      } catch (error) {
        if (error.response.status == 422) {
          this.loadStandardErrorMessages({
            title: error.response.data.error_title
          });
        }
      }
      crop_field_certification.proc_button_destroy = false;
    },
    expiredAt(crop_field_certification) {
        let days = this.daysUntilExpirationDate(
          crop_field_certification.expiration_date
        );

        if (days > 0) {
          return `<span>
            <i class="fa-solid fa-circle-check text-green-500"></i>
            ${this.$t("labels.days", { days: this.numberWithPrecision(days) })}
            </span>`;
        } else {
          return `
            <span class="text-red-500">
            <i class="fa-solid fa-triangle-exclamation text-red-500"></i>
            ${this.$t("labels.expired_date")}
            </span>`;
        }
    },
    goToNewCropFieldCertifications() {
      this.$router.push({
        name: this.routeName("new_crop_field_certification"),
        params: {
          agro_exporter_id: this.agro_exporter_id,
          producer_id: this.producer_id
        }
      });
    },
    goToEditCropFieldCertification(crop_field_certification_id) {
      this.$router.push({
        name: this.routeName("edit_crop_field_certification"),
        params: {
          agro_exporter_id: this.agro_exporter_id,
          producer_id: this.producer_id,
          crop_field_certification_id: crop_field_certification_id
        }
      });
    }
  }
};
</script>
